import {
    NinchatProperties,
    NinchatStore,
} from 'api/types'

export type TicketState = 'done' | 'new' | 'open' | 'unassigned'

type TicketStateOptions = {
    acceptedAudienceIds?: ReadonlySet<NinchatProperties.AudienceID>
    activity: NinchatProperties.NinchatQueueActivity
    comments?: NinchatProperties.NinchatAudienceComment[]
    ticketingEnabled: boolean
    transcript?: NinchatStore.NinchatStoreTranscript
}

type PendingTicketOptions = Omit<TicketStateOptions, 'ticketingEnabled'> & {
    newOnly?: boolean
}

export const getAcceptedAudienceIds = (
    activities: NinchatProperties.NinchatQueueActivity[] | undefined,
): Set<NinchatProperties.AudienceID> => new Set(
    (activities || [])
        .filter((activity) => activity.event === 'audience_accepted' && activity.audience_id)
        .map((activity) => activity.audience_id as NinchatProperties.AudienceID),
)

export const getLatestTicketChangeComment = (
    comments: NinchatProperties.NinchatAudienceComment[] | undefined,
    change?: NinchatProperties.CommentChange,
): NinchatProperties.NinchatAudienceComment | undefined => comments?.reduce<NinchatProperties.NinchatAudienceComment | undefined>((latest, comment) => {
    const isDifferentChange = change !== undefined && comment.change !== change

    if (!comment.change || isDifferentChange) {
        return latest
    }

    if (!latest || comment.time > latest.time) {
        return comment
    }

    return latest
}, undefined)

export const getLatestTicketChange = (
    comments: NinchatProperties.NinchatAudienceComment[] | undefined,
): NinchatProperties.CommentChange | undefined => getLatestTicketChangeComment(comments)?.change

const hasTicketChangeHistory = (
    comments: NinchatProperties.NinchatAudienceComment[] | undefined,
): boolean => (comments || []).some((comment) => comment.change === 'assigned' || comment.change === 'done' || comment.change === 'unassigned')

export const isTicketDone = (
    comments: NinchatProperties.NinchatAudienceComment[] | undefined,
    transcript: NinchatStore.NinchatStoreTranscript | undefined,
): boolean => transcript?.transcript_status === 'done' || getLatestTicketChange(comments) === 'done'

export const getTicketState = ({
    acceptedAudienceIds,
    activity,
    comments,
    ticketingEnabled,
    transcript,
}: TicketStateOptions): TicketState | undefined => {
    if (!ticketingEnabled || activity.event !== 'audience_complete' || !activity.audience_id) {
        return undefined
    }

    const latestChange = getLatestTicketChange(comments)

    if (isTicketDone(comments, transcript)) {
        return 'done'
    }

    if (transcript?.assign_user_id) {
        return 'open'
    }

    if (!hasTicketChangeHistory(comments)) {
        return 'new'
    }

    if (transcript && !transcript.assign_user_id) {
        return 'unassigned'
    }

    if (latestChange === 'assigned') {
        return 'open'
    }

    if (latestChange === 'unassigned') {
        return 'unassigned'
    }

    if (acceptedAudienceIds?.has(activity.audience_id)) {
        return undefined
    }

    if (!transcript?.assign_user_id) {
        return 'unassigned'
    }

    return undefined
}

export const isPendingTicket = ({
    newOnly,
    ...options
}: PendingTicketOptions): boolean => {
    const ticketState = getTicketState({
        ...options,
        ticketingEnabled: true,
    })

    return newOnly ? ticketState === 'new' : ticketState === 'new' || ticketState === 'unassigned'
}
